'use no memo'

import { useMemo } from 'react'
import { Link, Outlet, createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router'
import { ProjectProvider } from '../context/ProjectContext'
import { ProjectTabContent } from '../components/ProjectTabContent'
import { useLayoutRouteState } from '../hooks/useProjectChildRouteType'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { StarToggle } from '@/components/app/StarToggle'
import { useProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Loading } from '@/components/app/indicators/Loading'
import { usePageLayout } from '@/hooks/usePageLayout'
import TaskSidebar from '@/components/app/sidebars/task'
import { ProjectIcon } from '@/lib/utils/projectIcon'

type ProjectBaseTab = 'tasks' | 'pages' | 'cycles'
const PROJECT_BASE_TABS: Array<ProjectBaseTab> = ['tasks', 'pages', 'cycles']

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/projects/$projectKey',
)({
  component: ProjectLayout,
  validateSearch: (search: Record<string, unknown>): { projectTab?: ProjectBaseTab } => {
    const tab = (search.projectTab ?? search.tab) as string | undefined
    if (tab && PROJECT_BASE_TABS.includes(tab as ProjectBaseTab)) {
      return { projectTab: tab as ProjectBaseTab }
    }
    return {}
  },
})

export default function ProjectLayout() {
  const { workspaceSlug, projectKey: projectKeyParam } = Route.useParams()
  const projectKey = projectKeyParam.toUpperCase()
  const navigate = useNavigate()
  const workspaceId = useLoginStore((state) => state.workspaceId)

  const basePath = `/${workspaceSlug}/projects/${projectKeyParam}`
  const { isActive, childRouteType } = useLayoutRouteState(basePath)

  // Guard: TanStack Router may incorrectly activate this layout for sibling
  // space routes that share a child path pattern (e.g. /pages/$pageSlug).
  // When the URL doesn't belong to /$workspaceSlug/projects/*, bail out.
  const isWrongRoute = useRouterState({
    select: (state) => {
      const pathname = state.location.pathname
      return !pathname.startsWith(`/${workspaceSlug}/projects/`)
    },
  })
  if (isWrongRoute) return <Outlet />

  const { projects, isLoading: projectsLoading } = useProjects()

  const project = useMemo<ProjectDocument | null>(() => {
    return (
      projects.find((entry) => {
        if (entry.isDeleted) return false
        if (entry.projectKey !== projectKey) return false
        if (workspaceId && entry.envId !== workspaceId) return false
        return true
      }) ?? null
    )
  }, [projects, projectKey, workspaceId])

  // Breadcrumb
  const breadcrumbContent = useMemo(() => {
    if (!project) return null
    return (
      <>
        <Link
          to="/$workspaceSlug/projects/$projectKey"
          params={{ workspaceSlug, projectKey: projectKeyParam }}
          className="flex items-center gap-1 hover:text-foreground transition-colors truncate"
        >
          <ProjectIcon iconType={project.iconType} icon={project.icon} iconColor={project.iconColor} size={14} />
          <span className="truncate">{project.name || 'Untitled project'}</span>
        </Link>
        <StarToggle entityType="project" entityId={project.id} size={14} />
      </>
    )
  }, [project, workspaceSlug, projectKeyParam])

  usePageLayout({
    sidebarContent: <TaskSidebar />,
    ...(childRouteType === 'other'
      ? {}
      : { breadcrumb: breadcrumbContent }),
  }, [project?.id, project?.name, project?.icon, project?.iconType, project?.iconColor, workspaceSlug, projectKeyParam, childRouteType])

  // During route transitions the router updates location before unmounting
  // stale child layouts — bail out so our Navigate doesn't race the parent's.
  if (!isActive) return null

  if (childRouteType === 'other' || childRouteType === 'views') return <Outlet />

  if (projectsLoading) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!project) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center px-4">
        <div className="text-center text-muted-foreground">
          Project not found or may have been removed.
        </div>
      </div>
    )
  }

  return (
    <ProjectProvider projectId={project.id}>
      <ProjectTabContent
        projectId={project.id}
        projectKey={project.projectKey.toLowerCase()}
        workspaceSlug={workspaceSlug}
        onCycleClick={(cycleId) => {
          navigate({
            to: '/$workspaceSlug/projects/$projectKey/cycles/$cycleId',
            params: { workspaceSlug, projectKey: projectKeyParam, cycleId },
          })
        }}
      />
    </ProjectProvider>
  )
}

export type { ProjectBaseTab }
